// NOSONAR
import React, { useState, useEffect, useRef } from 'react';

const DatePicker = ({ value, onChange, label, required = false, minDate, maxDate }) => {
  // ---- allowed window: today-6 .. today  (previous 7 days inclusive) ----
  const now = new Date();

  let allowedMin;
  if (minDate) {
    const [y, m, d] = minDate.split('-');
    allowedMin = new Date(y, m - 1, d, 0, 0, 0, 0);
  } else {
    allowedMin = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  }

  let allowedMax;
  if (maxDate) {
    const [y, m, d] = maxDate.split('-');
    allowedMax = new Date(y, m - 1, d, 23, 59, 59, 999);
  } else {
    allowedMax = allowedMin;
  }

  // state
  const [isOpen, setIsOpen] = useState(false);
  const [selectedDay, setSelectedDay] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedYear, setSelectedYear] = useState('');

  // scroll refs attached to .column-scroll elements (reliable event targets)
  const dayScrollRef = useRef(null);
  const monthScrollRef = useRef(null);
  const yearScrollRef = useRef(null);

  // latest values to avoid stale closures inside scroll handlers
  const selectedDayRef = useRef(selectedDay);
  const selectedMonthRef = useRef(selectedMonth);
  const selectedYearRef = useRef(selectedYear);
  useEffect(() => { selectedDayRef.current = selectedDay; }, [selectedDay]);
  useEffect(() => { selectedMonthRef.current = selectedMonth; }, [selectedMonth]);
  useEffect(() => { selectedYearRef.current = selectedYear; }, [selectedYear]);

  // raw lists
  // Helper to get days in a month/year
  const getDaysInMonth = (month, year) => {
    if (!month || !year) return 31;
    return new Date(parseInt(year, 10), parseInt(month, 10), 0).getDate();
  };

  // Compute days for selected month/year
  const React = require('react');
  const { useMemo } = React;
  const daysRaw = useMemo(() => {
    if (selectedMonth && selectedYear) {
      const daysCount = getDaysInMonth(selectedMonth, selectedYear);
      return Array.from({ length: daysCount }, (_, i) => String(i + 1).padStart(2, '0'));
    }
    return Array.from({ length: 31 }, (_, i) => String(i + 1).padStart(2, '0'));
  }, [selectedMonth, selectedYear]);
  const monthsRaw = [
    { value: '01', label: 'January' }, { value: '02', label: 'February' },
    { value: '03', label: 'March' }, { value: '04', label: 'April' },
    { value: '05', label: 'May' }, { value: '06', label: 'June' },
    { value: '07', label: 'July' }, { value: '08', label: 'August' },
    { value: '09', label: 'September' }, { value: '10', label: 'October' },
    { value: '11', label: 'November' }, { value: '12', label: 'December' }
  ];
  const currentYear = new Date().getFullYear();
  const yearsRaw = Array.from({ length: 6 }, (_, i) => String(currentYear - 2 + i)); // small span around current year is enough

  // buffered lists and buffer size
  const [days, setDays] = useState([]);
  const [months, setMonths] = useState([]);
  const [years, setYears] = useState([]);
  const [buffer, setBuffer] = useState(3);

  // debounce timers
  const debounceTimers = useRef({ day: null, month: null, year: null });

  // init from value prop — keep empty if no value provided
  useEffect(() => {
    if (value) {
      const [y = '', m = '', d = ''] = (value || '').split('-');
      setSelectedYear(y);
      setSelectedMonth(m);
      setSelectedDay(d);
    } else {
      setSelectedYear('');
      setSelectedMonth('');
      setSelectedDay('');
    }
  }, [value]);

  // ----- helper validators using allowedMin/allowedMax -----
  const isYearDisabled = (year) => {
    if (!year) return false; // allow choosing year
    const y = parseInt(year, 10);
    const first = new Date(y, 0, 1);
    const last = new Date(y, 11, 31, 23, 59, 59, 999);
    return last < allowedMin || first > allowedMax;
  };

  const isMonthDisabled = (month, year) => {
    if (!month) return false;            // allow selecting month before year
    if (!year) return false;
    const y = parseInt(year, 10);
    const m = parseInt(month, 10);
    const first = new Date(y, m - 1, 1, 0, 0, 0, 0);
    const last = new Date(y, m, 0, 23, 59, 59, 999);
    return last < allowedMin || first > allowedMax;
  };

  // require full date to be checked — otherwise day entries remain disabled until month+year are chosen
  const isDateDisabled = (day, month, year) => {
    if (!day || !month || !year) return true;
    const d = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10), 12, 0, 0, 0);
    return d < allowedMin || d > allowedMax;
  };

  // emit final date only when all parts present and valid
  const emitIfComplete = (d, m, y) => {
    if (!d || !m || !y) return;
    if (isDateDisabled(d, m, y)) return;
    const daysInMonth = new Date(parseInt(y, 10), parseInt(m, 10), 0).getDate();
    const validDay = Math.min(parseInt(d, 10), daysInMonth);
    const dd = String(validDay).padStart(2, '0');
    onChange && onChange(`${y}-${m}-${dd}`);
  };

  // unified select handler
  const handleSelect = (type, value) => {
    if (!value) return;
    if (type === 'year') {
      if (isYearDisabled(value)) return;
      setSelectedYear(value);

      // clear month/day if they become invalid for new year
      if (selectedMonth && isMonthDisabled(selectedMonth, value)) {
        setSelectedMonth('');
        setSelectedDay('');
      } else if (selectedMonth && isDateDisabled(selectedDay, selectedMonth, value)) {
        setSelectedDay('');
      }

      emitIfComplete(selectedDay, selectedMonth, value);
      return;
    }
    if (type === 'month') {
      if (isMonthDisabled(value, selectedYear)) return;
      setSelectedMonth(value);
      if (selectedYear && isDateDisabled(selectedDay, value, selectedYear)) setSelectedDay('');
      emitIfComplete(selectedDay, value, selectedYear);
      return;
    }
    // day
    if (type === 'day') {
      // disallow day selection until month+year chosen (isDateDisabled returns true if incomplete)
      if (isDateDisabled(value, selectedMonth, selectedYear)) return;
      setSelectedDay(value);
      emitIfComplete(value, selectedMonth, selectedYear);
      return;
    }
  };

  // when opened, preselect today if nothing is selected yet
  useEffect(() => {
  if (isOpen) {
    if (!selectedDay || !selectedMonth || !selectedYear) {

      const minYear = String(allowedMin.getFullYear());
      const minMonth = String(allowedMin.getMonth() + 1).padStart(2,'0');
      const minDay = String(allowedMin.getDate()).padStart(2,'0');

      setSelectedYear(minYear);
      setSelectedMonth(minMonth);
      setSelectedDay(minDay);

      onChange && onChange(`${minYear}-${minMonth}-${minDay}`);
    }
  }
}, [isOpen]);

  // build buffered arrays when opening or when selected month/year changes
  useEffect(() => {
    if (!isOpen) return;

    // compute buffer from first available scroller to keep centering intact
    let bufferSize = 7;
    const refs = [dayScrollRef, monthScrollRef, yearScrollRef];
    for (const r of refs) {
      if (r.current) {
        const sample = r.current.querySelector('.date-item[data-value]');
        if (sample) {
          const h = sample.offsetHeight;
          const H = r.current.offsetHeight;
          if (h && H) { bufferSize = Math.ceil(H / h / 2) + 3; break; }
        }
      }
    }
    setBuffer(bufferSize);
    const head = Array(bufferSize).fill('');
    const tail = Array(bufferSize).fill('');

    // days: if month + year chosen, populate only allowed days in that month; otherwise use daysRaw but they will be disabled
    let daysArr = [];
    if (selectedMonth && selectedYear) {
      const dim = new Date(parseInt(selectedYear, 10), parseInt(selectedMonth, 10), 0).getDate();
      for (let i = 1; i <= dim; i++) {
        const ds = String(i).padStart(2, '0');
        if (!isDateDisabled(ds, selectedMonth, selectedYear)) daysArr.push(ds);
      }
    } else {
      daysArr = daysRaw.slice();
    }

    setDays([...head, ...daysArr, ...tail]);
    setMonths([
      ...head.map(() => ({ value: '', label: '' })),
      ...monthsRaw,
      ...tail.map(() => ({ value: '', label: '' }))
    ]);
    setYears([...head, ...yearsRaw, ...tail]);

    // center currently selected values (if any)
    const t = setTimeout(() => {
      const center = (ref, val) => {
        if (!ref.current || !val) return;
        const el = ref.current.querySelector(`[data-value="${val}"]`);
        if (!el) return;
        ref.current.scrollTop = el.offsetTop - (ref.current.clientHeight / 2) + (el.offsetHeight / 2);
      };
      center(dayScrollRef, selectedDay);
      center(monthScrollRef, selectedMonth);
      center(yearScrollRef, selectedYear);
    }, 120);

    return () => clearTimeout(t);
  }, [isOpen, selectedMonth, selectedYear]); // re-run when month/year changes

  // parallax & auto-select (debounced) while scrolling
  const applyParallaxAndAutoSelect = (scrollerRef, type) => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const items = Array.from(scroller.querySelectorAll('.date-item')).filter(it => it.getAttribute('data-value'));
    if (!items.length) return;

    const center = scroller.scrollTop + scroller.clientHeight / 2;
    let closest = null, minDist = Infinity;

    items.forEach(item => {
      const itemCenter = item.offsetTop + item.offsetHeight / 2;
      const dist = Math.abs(itemCenter - center);
      if (dist < minDist) { minDist = dist; closest = item; }
    });

    if (!closest) return;
    const value = closest.getAttribute('data-value');
    if (!value) return;

    // 🔒 Prevent scroll stop on disabled item
    let disabled = false;
    if (type === 'day') disabled = isDateDisabled(value, selectedMonthRef.current, selectedYearRef.current);
    if (type === 'month') disabled = isMonthDisabled(value, selectedYearRef.current);
    if (type === 'year') disabled = isYearDisabled(value);

    if (disabled) {
      // snap back to last valid selection
      const sel = scroller.querySelector(`[data-value="${{
        day: selectedDayRef.current,
        month: selectedMonthRef.current,
        year: selectedYearRef.current
      }[type]}"]`);
      if (sel) {
        scroller.scrollTop = sel.offsetTop - (scroller.clientHeight / 2) + (sel.offsetHeight / 2);
      }
      return; // stop here
    }

    // existing debounce + auto-select
    if (debounceTimers.current[type]) clearTimeout(debounceTimers.current[type]);
    debounceTimers.current[type] = setTimeout(() => {
      if (type === 'day') {
        setSelectedDay(value);
        emitIfComplete(value, selectedMonthRef.current, selectedYearRef.current);
      } else if (type === 'month') {
        setSelectedMonth(value);
        emitIfComplete(selectedDayRef.current, value, selectedYearRef.current);
      } else if (type === 'year') {
        setSelectedYear(value);
        emitIfComplete(selectedDayRef.current, selectedMonthRef.current, value);
      }
    }, 120);
  };


  // attach scroll listeners to the actual scrollers
  useEffect(() => {
    if (!isOpen) return;
    const cfg = [
      { ref: dayScrollRef, type: 'day' },
      { ref: monthScrollRef, type: 'month' },
      { ref: yearScrollRef, type: 'year' }
    ];
    const cleanups = [];

    cfg.forEach(({ ref, type }) => {
      const scroller = ref.current;
      if (!scroller) return;
      const onScroll = () => applyParallaxAndAutoSelect(ref, type);
      scroller.addEventListener('scroll', onScroll, { passive: true });
      setTimeout(onScroll, 0);
      cleanups.push(() => scroller.removeEventListener('scroll', onScroll));
    });

    return () => cleanups.forEach(fn => fn && fn());
  }, [isOpen, days.length, months.length, years.length]);

  // center selected when things change
  useEffect(() => {
    if (!isOpen) return;
    const t = setTimeout(() => {
      const center = (ref, val) => {
        if (!ref.current || !val) return;
        const el = ref.current.querySelector(`[data-value="${val}"]`);
        if (!el) return;
        ref.current.scrollTop = el.offsetTop - (ref.current.clientHeight / 2) + (el.offsetHeight / 2);
      };
      center(dayScrollRef, selectedDayRef.current);
      center(monthScrollRef, selectedMonthRef.current);
      center(yearScrollRef, selectedYearRef.current);
    }, 120);
    return () => clearTimeout(t);
  }, [isOpen, selectedDay, selectedMonth, selectedYear, days.length, months.length, years.length]);

  // render column items; buffer entries have empty value so they're ignored
  const renderColumnItems = (type, items, selected) => items.map((item, i) => {
    const value = type === 'month' ? (item?.value || '') : (item || '');
    const label = type === 'month' ? (item?.label || '') : (item || '');
    const isSelected = value === selected;
    const disabled = value ? (
      type === 'day' ? isDateDisabled(value, selectedMonth, selectedYear)
        : type === 'month' ? isMonthDisabled(value, selectedYear)
          : isYearDisabled(value)
    ) : false;

    const dataValue = value || undefined; // no data-value for buffer entries

    return (
      <div
        key={`${type}-${i}`}
        className={`date-item ${isSelected ? 'selected' : ''} ${disabled ? 'disabled' : ''}`}
        data-value={dataValue}
        role="option"
        aria-selected={isSelected}
        tabIndex={isSelected ? 0 : -1}
        onClick={() => !disabled && value && handleSelect(type, value)}
        onKeyDown={(e) => !disabled && value && (e.key === 'Enter' || e.key === ' ') && handleSelect(type, value)}
      >
        {label || ' '}
      </div>
    );
  });

  const columns = [
    { type: 'day', ref: dayScrollRef, items: days, selected: selectedDay, aria: 'Days' },
    { type: 'month', ref: monthScrollRef, items: months, selected: selectedMonth, aria: 'Months' },
    { type: 'year', ref: yearScrollRef, items: years, selected: selectedYear, aria: 'Years' }
  ];

  // display empty unless full date selected
  const formatDisplay = () => {
    if (selectedDay && selectedMonth && selectedYear) {
      const mn = monthsRaw.find(m => m.value === selectedMonth)?.label || '';
      return `${selectedDay} ${mn} ${selectedYear}`;
    }
    return 'Select Date';
  };

  const handleKeyDownOpen = (e) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setIsOpen(true); }
  };

  return (
    <>
      <div className="custom-date-picker">
        <div
          className="invoice-date"
          role="button"
          tabIndex={0}
          onClick={() => setIsOpen(true)}
          onKeyDown={handleKeyDownOpen}
          aria-label="Open date picker"
          style={{ cursor: 'pointer' }}
        >
          <span className="small">{formatDisplay()}</span>
          <i className="icon-timecalendar01 float-end invoice-calendar" />
        </div>
      </div>

      {isOpen && (
        <div
          id="datepicker-dialog"
          role="dialog"
          aria-modal="true"
          className="datepicker-overlay"
          onClick={() => setIsOpen(false)}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setIsOpen(false);
            }
          }}
        >
          <div
            className="date-picker-modal"
            onClick={(e) => e.stopPropagation()}
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.stopPropagation();
              }
            }}
          >
            <div className="date-picker-header">
              <h6>{label || 'Select Date'}</h6>
              <button type="button" className="btn-close" onClick={() => setIsOpen(false)} />
            </div>

            <div className="datepicker-container" role="document" tabIndex={0}>
              <div className="columns-container">
                {columns.map(({ type, ref, items, selected, aria }) => (
                  <div className="column" key={type}>
                    <div className="column-scroll" ref={ref} tabIndex={0} role="listbox" aria-label={aria}>
                      {renderColumnItems(type, items, selected)}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="date-picker-footer">
              <button type="button" className="btn btn-primary w-100" onClick={() => setIsOpen(false)}>Confirm</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DatePicker;